import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import AdminModal from './AdminModal';
import GradientProgressBar from '../../../shared/components/GradientProgressBar';

export default function SelfClosingModal({
  show,
  onClose,
  title,
  children,
  autoCloseDelay = 5000,
  pauseOnHover = true,
  footer,
  size = '',
  progressVariant = 'primary',
}) {
  const [timeRemaining, setTimeRemaining] = useState(autoCloseDelay);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (show) {
      setTimeRemaining(autoCloseDelay);
      setIsPaused(false);
    }
  }, [show, autoCloseDelay]);

  useEffect(() => {
    if (show && !isPaused && timeRemaining > 0) {
      intervalRef.current = setInterval(() => {
        setTimeRemaining((prev) => Math.max(0, prev - 100));
      }, 100);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  }, [show, isPaused, timeRemaining]);

  useEffect(() => {
    if (show && timeRemaining <= 0) {
      onClose();
    }
  }, [show, timeRemaining, onClose]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const handleMouseEnter = () => {
    if (pauseOnHover) setIsPaused(true);
  };

  const handleMouseLeave = () => {
    if (pauseOnHover) setIsPaused(false);
  };

  const progressPercentage = (timeRemaining / autoCloseDelay) * 100;

  const defaultFooter = (
    <div className="d-flex justify-content-between align-items-center">
      <small className="text-muted text-center flex-grow-1">
        {pauseOnHover && 'Hover over the window to keep it from closing'}
      </small>
      <button type="button" className="btn btn-secondary" onClick={onClose}>
        Close
      </button>
    </div>
  );

  return (
    <AdminModal
      show={show}
      onClose={onClose}
      title={title}
      footer={footer || defaultFooter}
      size={size}
    >
      <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        {children}
        <div className="mt-3 mb-2">
          <GradientProgressBar
            value={progressPercentage}
            variant={progressVariant}
            size="sm"
            animated={!isPaused}
            ariaLabel="Time remaining before auto-close"
          />
        </div>
      </div>
    </AdminModal>
  );
}

SelfClosingModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  autoCloseDelay: PropTypes.number,
  pauseOnHover: PropTypes.bool,
  footer: PropTypes.node,
  size: PropTypes.oneOf(['sm', '', 'lg', 'xl']),
  progressVariant: PropTypes.oneOf(['purple-green', 'green', 'purple', 'primary']),
};
